import React from 'react';
import styled from 'styled-components';
import { Modal as RapydModal } from '@rapyd/react-ui-library';
import {
  ButtonVariants,
  SquaredButtonCss,
  TextCss,
  getTextColorByContrast
} from 'components/core/Button/Button';
import { ModalProps } from '@rapyd/react-ui-library/dist/components/Modal';
import { ThemeBreakpoints, ThemeFamilies } from 'models';

export type IModalProps = ModalProps;

const Modal = ({
  className,
  name,
  children,
  staticSize = ThemeBreakpoints.SMALL,
  themeFamily = ThemeFamilies.PRIMARY,
  primaryButtonProps,
  secondaryButtonProps,
  ...rest
}: IModalProps) => {
  const updatedPrimaryButtonProps = primaryButtonProps && {
    ...primaryButtonProps,
    name: 'primary-button',
    variant: ButtonVariants.BORDERED,
    themeFamily: primaryButtonProps.themeFamily || ThemeFamilies.PRIMARY
  };
  const updatedSecondaryButtonProps = secondaryButtonProps && {
    ...secondaryButtonProps,
    name: 'secondary-button',
    variant: ButtonVariants.OUTLINED,
    themeFamily: secondaryButtonProps.themeFamily || ThemeFamilies.SECONDARY
  };

  return (
    <StyledModal
      className={className}
      name={name || 'modal'}
      staticSize={staticSize}
      themeFamily={themeFamily}
      primaryButtonProps={updatedPrimaryButtonProps}
      secondaryButtonProps={updatedSecondaryButtonProps}
      {...rest}
    >
      {children}
    </StyledModal>
  );
};

export default Modal;

export const ModalButtonTextColorSetter =
  (buttonKey: 'primaryButtonProps' | 'secondaryButtonProps') =>
  (modalProps: ModalProps & { theme: Record<string, any> }) => {
    const buttonProps = modalProps[buttonKey];
    return getTextColorByContrast({
      ...buttonProps,
      theme: modalProps.theme
    });
  };

const StyledModal = styled(RapydModal)`
  &[data-testid$='modal-container'] {
    border-radius: 1.6rem;
  }

  > div:nth-child(3) {
    button[data-testid='primary-button'],
    button[data-testid='secondary-button'] {
      height: 3.8rem;
      border-radius: 0.6rem;
    }

    button[data-testid='primary-button'] {
      white-space: unset;
      ${SquaredButtonCss};
      ${ModalButtonTextColorSetter('primaryButtonProps')};
    }

    button[data-testid='secondary-button'] {
      ${TextCss};
      white-space: unset;
      border-radius: 0.6rem;
      border-color: transparent;
      background: ${({ theme }) => theme.colors.grey.default};
      color: ${({ theme }) => theme.colors.surface1};

      &:not(:hover) {
        ${ModalButtonTextColorSetter('secondaryButtonProps')};
      }
    }
  }

  h5[data-testid$='modal-title'] {
    color: ${({ theme }) => theme.colors.secondaryText};
    margin: unset;
  }
`;
